import React from "react";
import {Image, ScrollView, StyleProp, StyleSheet, ViewStyle} from "react-native";
import {LinearGradient} from "expo-linear-gradient";
import FontAwesome5 from "@expo/vector-icons/FontAwesome5";
import MaterialIcons from "@expo/vector-icons/MaterialIcons";
import Btn from "../../components/Btn";
import Div from "../../lib/Div";
import P from "../../lib/P";
import signatureSurface from "../../assets/img/wallet-signature-surface-native.png";
import withdrawErrorGlow from "../../assets/img/withdraw-error-glow.png";
import withdrawSuccessGlow from "../../assets/img/withdraw-success-glow.png";
import {colors, layout, signaturePattern, totalSteps} from "./theme";
import {FlowSummaryLine} from "./types";

type FlowHeaderProps = { title: string, currentStep: number, onBack: () => void, onClose: () => void };

type MethodRowProps = {
  icon: string, selected: boolean, title: string, subtitle: string, eta: string, etaWarn?: boolean, feeLabel: string,
  netAmountLabel: string, recommended?: boolean, disabled?: boolean, showAmounts?: boolean, onPress: () => void
};

type DestinationRowProps = {
  icon: string, selected?: boolean, title: string, subtitle: string, disabled?: boolean, variant?: 'default' | 'add',
  onPress: () => void
};

type FlowSummaryProps = { lines: FlowSummaryLine[], total?: FlowSummaryLine };

type SummaryLineProps = FlowSummaryLine & { total?: boolean };

type PrimaryButtonProps = { label: string, icon?: string, onPress: () => void, disabled?: boolean };

type ResultGlowProps = { tone?: 'success' | 'error', opacity?: number, exactStyle?: StyleProp<ViewStyle> };

type ResultIconProps = {
  icon: string, color: string, bg: string, borderColor?: string, size?: number, boxSize?: number, rounded?: number,
  exactStyle?: StyleProp<ViewStyle>
};

export const StepStack = ({children}: { children: React.ReactNode }) => <Div gap={layout.sectionGap}>{children}</Div>;

export const SignatureResultSurface = ({children, scroll}: { children: React.ReactNode; scroll?: boolean }) => {
  const content = (
    <Div flex={1} w="100%" maxW={layout.mobileMax} alignSelf="center" px={layout.mobileGutter} pt={32} pb={24}
         zIndex={1}>
      {children}
    </Div>
  );

  return (
    <Div style={flowStyles.signatureSurface}>
      <SignaturePatternLayer/>
      {scroll ? (
        <ScrollView style={flowStyles.scroll} contentContainerStyle={flowStyles.signatureScrollContent}
                    showsVerticalScrollIndicator={false}>
          {content}
        </ScrollView>
      ) : content}
    </Div>
  );
};

export const SignaturePatternLayer = () => (
  <Div pointerEvents="none" style={flowStyles.signaturePatternLayer}>
    {signaturePattern === 'fullSwing' ? (
      <LinearGradient
        colors={[colors.headerBg, colors.signatureBg]} start={{x: 0.5, y: 0}} end={{x: 0.5, y: 1}}
        style={flowStyles.fullSwingSignatureGradient}
      />
    ) : (
      <Image source={signatureSurface as any} resizeMode="contain" style={flowStyles.signaturePatternImage}/>
    )}
  </Div>
);

export const ResultGlow = ({tone = 'success', opacity, exactStyle}: ResultGlowProps) => (
  <Div pointerEvents="none" style={[flowStyles.resultGlow, opacity !== undefined ? {opacity} : undefined, exactStyle]}>
    <Image
      source={(tone === 'error' ? withdrawErrorGlow : withdrawSuccessGlow) as any} resizeMode="contain"
      style={[flowStyles.resultGlowImage, {tintColor: tone === 'error' ? colors.statusLost : colors.statusSuccess}]}
    />
  </Div>
);

export const ResultIcon = (props: ResultIconProps) => {
  const {icon, color, bg, borderColor = bg, size = 44, boxSize = 80, rounded = 24, exactStyle} = props;

  return (
    <Div w={boxSize} h={boxSize} rounded={rounded} bg={bg} borderWidth={1} borderColor={borderColor} alignItems="center"
         justifyContent="center" style={[flowStyles.resultIcon, exactStyle]}>
      <MaterialIcons name={icon as any} size={size} color={color}/>
    </Div>
  );
};

export const FlowHeader = ({title, currentStep, onBack, onClose}: FlowHeaderProps) => (
  <Div bg={colors.headerBg} px={layout.flowHeaderPaddingHorizontal} pt={layout.flowHeaderPaddingTop}
       pb={layout.flowHeaderPaddingBottom} zIndex={30}>
    <Div row alignItems="center" justifyContent="space-between">
      <Btn onPress={onBack} w={layout.flowHeaderButton} h={layout.flowHeaderButton} rounded={999} alignItems="center"
           justifyContent="center" accessibilityRole="button" accessibilityLabel="Back">
        <FontAwesome5 name="arrow-left" size={24} color={colors.textPrimary}/>
      </Btn>

      <Btn onPress={onClose} w={layout.flowHeaderButton} h={layout.flowHeaderButton} rounded={999} alignItems="center"
           justifyContent="center" accessibilityRole="button" accessibilityLabel="Close">
        <FontAwesome5 name="times" size={24} color={colors.textPrimary}/>
      </Btn>
    </Div>

    <Div alignItems="center" mt={layout.flowHeaderHeadMarginTop} px={8}>
      <Div row alignItems="center" justifyContent="center" gap={layout.stepDotGap} mb={layout.flowHeaderHeadGap}>
        {Array.from({length: totalSteps}).map((_, index) => (
          <Div
            key={index} w={index === currentStep - 1 ? layout.stepDotActiveWidth : layout.stepDotWidth} rounded={999}
            bg={index === currentStep - 1 ? colors.accent : colors.hairlineStrong} h={layout.stepDotHeight}
          />
        ))}
      </Div>

      <P fontFamily="DavisSans-Regular" fs={layout.flowHeaderTitleSize} lh={33.6} ta="center"
         color={colors.textPrimary}>
        {title}
      </P>
    </Div>
  </Div>
);

export const MethodRow = (props: MethodRowProps) => {
  const {
    icon, selected, title, subtitle, eta, etaWarn, feeLabel, netAmountLabel, recommended, disabled,
    showAmounts = true
  } = props;

  return (
    <Btn onPress={disabled ? undefined : props.onPress} disabled={disabled} position="relative" borderWidth={1}
         borderColor={selected ? colors.accent : colors.surfaceBorder} rounded={14} px={16} py={14}
         bg={selected ? colors.accentSoft : colors.bgElev1}>
      {recommended && (
        <Div position="absolute" top={-9} right={14} zIndex={1} bg={colors.accent} rounded={999} px={9} py={3}>
          <P fontFamily="DavisSans-Medium" fs={9} lh={13.5} tt="uppercase" color={colors.accentInk}>Recommended</P>
        </Div>
      )}

      <Div row alignItems="flex-start" gap={layout.rowGap}>
        <Div w={40} h={40} rounded={999} bg={colors.bgInset} alignItems="center" justifyContent="center"
             overflow="hidden">
          <FontAwesome5 name={icon as any} size={20} color={colors.accent}/>
        </Div>

        <Div flex={1} flexShrink={1} minW={0} gap={3}>
          <P fontFamily="DavisSans-Medium" fs={15} lh={22.5} color={colors.textPrimary}>{title}</P>
          <P fontFamily="DavisSans-Regular" fs={12} lh={16.8} color={colors.textSecondary}>{subtitle}</P>
          <P fontFamily="DavisSans-Medium" fs={11} lh={16.5} color={etaWarn ? colors.statusWarn : colors.accentText}
             tt="uppercase" mt={3}>
            {eta}
          </P>
        </Div>

        <Div alignItems="flex-end" gap={2} flexShrink={0}>
          {showAmounts && (
            <>
              <P fontFamily="DavisSans-Medium" fs={14} lh={21}
                 color={feeLabel === 'Free' ? colors.accentText : colors.textPrimary}>
                {feeLabel}
              </P>

              <P fontFamily="DavisSans-Regular" fs={11} lh={16.5} color={colors.textMuted}>Get {netAmountLabel}</P>
            </>
          )}

          <Div w={18} h={18} rounded={999} borderWidth={1.5} mt={6} alignItems="center" justifyContent="center"
               borderColor={selected ? colors.accent : colors.hairlineStrong}
               bg={selected ? colors.bgElev1 : undefined}>
            {selected && <Div w={12} h={12} rounded={999} bg={colors.accent}/>}
          </Div>
        </Div>
      </Div>
    </Btn>
  );
};

export const DestinationRow = (props: DestinationRowProps) => {
  const {icon, selected, title, subtitle, disabled, variant = 'default', onPress} = props;
  const isAddRow = variant === 'add';

  return (
    <Btn onPress={disabled ? undefined : onPress} disabled={disabled} borderWidth={1} rounded={14} px={14} py={12}
         borderStyle={isAddRow ? 'dashed' : 'solid'}
         borderColor={selected || isAddRow ? colors.accent : colors.surfaceBorder}
         bg={selected ? colors.accentSoft : isAddRow ? 'transparent' : colors.bgElev1}
         opacity={disabled ? 0.55 : undefined} accessibilityState={{disabled}}>
      <Div row alignItems="center" gap={layout.rowGap}>
        <Div w={42}>
          <Div w={40} h={40} rounded={999} bg={colors.bgInset} alignItems="center" justifyContent="center"
               overflow="hidden">
            <FontAwesome5 name={icon as any} size={20} color={colors.accent}/>
          </Div>
        </Div>

        <Div flex={1} flexShrink={1} minW={0}>
          <P fontFamily="DavisSans-Medium" fs={14} lh={21} color={isAddRow ? colors.accentText : colors.textPrimary}>
            {title}
          </P>
          <P fontFamily="DavisSans-Regular" fs={11} lh={16.5} color={colors.textSecondary} mt={2}>{subtitle}</P>
        </Div>

        {selected ? (
          <FontAwesome5 name="check" size={20} color={colors.accent}/>
        ) : (
          <Div w={20}/>
        )}
      </Div>
    </Btn>
  );
};

export const CaptionInfo = ({text}: { text: string }) => (
  <Div row alignItems="flex-start" gap={8}>
    <FontAwesome5 name="info-circle" size={18} color={colors.textMuted} style={{marginTop: 1}}/>
    <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.textMuted} flex={1}>{text}</P>
  </Div>
);

export const InfoCard = ({icon, text}: { icon: string; text: string }) => (
  <Div row alignItems="flex-start" gap={12} bg={colors.bgInset} borderWidth={1} borderColor={colors.surfaceBorder}
       rounded={12} p={14}>
    <FontAwesome5 name={icon as any} size={20} color={colors.accent} style={{flexShrink: 0}}/>
    <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.textSecondary} flex={1}>{text}</P>
  </Div>
);

export const EtaCard = (props: { title: string, subtitle: string, icon?: string, warn?: boolean }) => {
  const {title, subtitle, icon = 'schedule', warn} = props;

  return (
    <Div row alignItems="center" gap={12} bg={colors.bgInset} borderWidth={1} rounded={14} py={14} px={16}
         borderColor={warn ? colors.statusWarnSoft : colors.accentSoftStrong}>
      <Div w={40} h={40} rounded={20} bg={warn ? colors.statusWarnSoft : colors.accentSoft} alignItems="center"
           justifyContent="center" flexShrink={0}>
        <MaterialIcons name={icon as any} size={20} color={warn ? colors.statusWarn : colors.accent}/>
      </Div>

      <Div flex={1}>
        <P fontFamily="DavisSans-Medium" fs={14} lh={21} color={colors.textPrimary}>{title}</P>
        <P fontFamily="DavisSans-Regular" fs={12} lh={18} color={colors.textSecondary} mt={2}>{subtitle}</P>
      </Div>
    </Div>
  );
};

export const FlowSummary = ({lines, total}: FlowSummaryProps) => (
  <Div bg={colors.bgElev1} borderWidth={1} borderColor={colors.surfaceBorder} rounded={16} px={18} py={4}>
    {lines.map(line => (
      <SummaryLine key={line.label} label={line.label} value={line.value} accent={line.accent}/>
    ))}

    {total && <SummaryLine label={total.label} value={total.value} total/>}
  </Div>
);

const SummaryLine = ({label, value, accent, total}: SummaryLineProps) => (
  <Div row justifyContent="space-between" alignItems="center" gap={12} pt={total ? 14 : 12} pb={12} mt={total ? 2 : 0}
       borderTopWidth={total ? 1 : 0} borderTopColor={colors.hairlineStrong} borderBottomWidth={total ? 0 : 1}
       borderBottomColor={colors.hairline}>
    <P fontFamily="DavisSans-Regular" fs={13} lh={19.5} color={colors.textSecondary}>{label}</P>
    <P fontFamily="DavisSans-Medium" fs={14} lh={21} color={accent ? colors.accentText : colors.textPrimary} ta="right"
       flex={1}>
      {value}
    </P>
  </Div>
);

export const PrimaryButton = ({label, icon, onPress, disabled}: PrimaryButtonProps) => (
  <Btn onPress={disabled ? undefined : onPress} disabled={disabled} w="100%" h={layout.buttonLgHeight}
       rounded={layout.buttonRadius}
       bg={colors.accent} alignItems="center" justifyContent="center" px={layout.buttonLgPaddingHorizontal}
       opacity={disabled ? 0.45 : undefined} accessibilityRole="button" accessibilityState={{disabled}}>
    <Div row alignItems="center" justifyContent="center" gap={10}>
      <P fontFamily="DavisSans-Medium" fs={14} lh={21} tt="uppercase" ta="center" color={colors.accentInk}>{label}</P>
      {icon && <FontAwesome5 name={icon as any} size={20} color={colors.accentInk}/>}
    </Div>
  </Btn>
);

export const GhostButton = ({label, onPress, disabled}: { label: string; onPress: () => void; disabled?: boolean }) => (
  <Btn onPress={disabled ? undefined : onPress} disabled={disabled} w="100%" h={layout.buttonLgHeight}
       rounded={layout.buttonRadius}
       alignItems="center" justifyContent="center" px={layout.buttonLgPaddingHorizontal} accessibilityRole="button"
       opacity={disabled ? 0.45 : undefined} accessibilityState={{disabled}}>
    <P fontFamily="DavisSans-Medium" fs={14} lh={21} tt="uppercase" ta="center" color={colors.accentText}>{label}</P>
  </Btn>
);

export const OutlineButton = ({label, onPress}: { label: string; onPress: () => void }) => (
  <Btn onPress={onPress} w="100%" h={layout.buttonLgHeight} rounded={layout.buttonRadius} bg={colors.bgInset}
       alignItems="center" justifyContent="center" px={layout.buttonLgPaddingHorizontal} accessibilityRole="button">
    <P fontFamily="DavisSans-Medium" fs={14} lh={21} tt="uppercase" ta="center" color={colors.textPrimary}>{label}</P>
  </Btn>
);

export const flowStyles = StyleSheet.create({
  scroll: {flex: 1},
  scrollContent: {
    paddingTop: layout.mobileGutter, paddingHorizontal: layout.mobileGutter, paddingBottom: layout.navHeight + 16
  },
  signatureSurface: {flex: 1, backgroundColor: colors.signatureBg, position: 'relative', overflow: 'hidden'},
  signaturePatternLayer: {position: 'absolute', top: 0, bottom: 0, left: 0, right: 0},
  signaturePatternImage: {position: 'absolute', top: 0, right: 0, width: '140%', aspectRatio: 16 / 9},
  fullSwingSignatureGradient: {position: 'absolute', top: 0, bottom: 0, left: 0, right: 0},
  signatureScrollContent: {flexGrow: 1, zIndex: 1},
  processingContent: {flexGrow: 1},
  resultBrandLogo: {width: 107, height: 28},
  resultGlow: {
    position: 'absolute', left: '50%', top: '50%', width: 200, height: 200, borderRadius: 999, marginLeft: -100,
    marginTop: -100
  },
  resultGlowImage: {width: '100%', height: '100%'},
  resultIcon: {position: 'relative'}
});
